// A BORE IS CUT OUT OF THE TERRAIN MESH. PURE module (no THREE, no DOM):
// the GROUND-M bake (groundm/bakectx.mjs cutNode) cuts every node lattice
// through it, and scripts/check-bore-cut.mjs / check-pave.mjs / check-groundm
// rebuild the same cuts in node with the very same code.
//
// The bore volume is the LINING's: per drawn segment (station i -> i+1) the
// convex hull of the two station rings — the arch points at floor + hAt(o)
// and the wall foot corners at floor + cf o - FLOOR_UNDER_M, placed on the
// station normal at the (bend-clamped) plan offset. A convex hull of points
// is exactly
//   its xz footprint (wall lines + the two section lines; at a portal the
//   end station's section line IS the portal plane)
//   AND y >= its lower hull (the floor)  AND y <= its upper hull (the arch),
// so each volume is a short list of half-spaces f(x, y, z) <= 0, the vertical
// ones first. Consecutive segments share their section line exactly: the
// union is gap-free. The station rings come from ways.json bores[k].cut (the
// prism list emit lined) or are derived from the bore's own stations +
// section (+ section.top, the capped roof).
//
// The CUT (cutTile): every terrain triangle that meets a volume is clipped
// in its own BARYCENTRIC domain against that volume's half-spaces and the
// outside pieces are kept as a fan. A new vertex is therefore a linear
// interpolation of its triangle's three vertices in every attribute (local
// x/z, uv, h): the piece stays in its triangle's own plane — a cut, never a
// new surface — and a point on an original edge keeps its third barycentric
// EXACTLY 0 (it lies on that edge: watertight against the neighbour, which
// removes the same interval of the shared edge). Skirt triangles (zero xz
// area) need no special case in that domain. A triangle outside every
// volume is copied verbatim, so the mesh outside the bores is the template
// byte for byte; a tile that meets no volume never gets here and keeps its
// template.
import { levelStride, strideSrc, quadIndex } from '../../game/lodmath.js';

import { FLOOR_UNDER_M, CLEAR_M, stationNormals, boreStations, boreVolumes, depthIn, depthInPlan, depthInAll, depthInPlanAll } from '../../game/borevol.js';
// the bore volume lives in game/borevol.js (shared with world/emit.js); re-exported here, so every importer of borecut is unchanged
export { FLOOR_UNDER_M, CLEAR_M, stationNormals, boreStations, boreVolumes, depthIn, depthInPlan, depthInAll, depthInPlanAll };
// a piece whose barycentric area is under this is not drawn (a sliver of
// float noise along a cut line; a triangle's own area is 0.5)
const MIN_BARY_AREA = 1e-12;


// ------------------------------------------------------------ the index
// uniform plan grid over the volumes' AABBs
export class VolumeIndex {
  constructor(vols, cell = 32) {
    this.vols = vols;
    this.cell = cell;
    this.map = new Map();
    this._stamp = new Uint32Array(vols.length);
    this._tick = 0;
    for (let i = 0; i < vols.length; i++) {
      const v = vols[i];
      for (let gz = Math.floor(v.z0 / cell); gz <= Math.floor(v.z1 / cell); gz++) {
        for (let gx = Math.floor(v.x0 / cell); gx <= Math.floor(v.x1 / cell); gx++) {
          const k = gx + ':' + gz;
          let a = this.map.get(k);
          if (!a) this.map.set(k, a = []);
          a.push(i);
        }
      }
    }
  }

  // volumes whose AABB meets the box (y optional)
  query(x0, x1, z0, z1, y0 = -Infinity, y1 = Infinity) {
    const out = [];
    if (!this.vols.length) return out;
    if (++this._tick === 0xFFFFFFFF) { this._stamp.fill(0); this._tick = 1; }
    const c = this.cell, t = this._tick;
    for (let gz = Math.floor(z0 / c); gz <= Math.floor(z1 / c); gz++) {
      for (let gx = Math.floor(x0 / c); gx <= Math.floor(x1 / c); gx++) {
        const a = this.map.get(gx + ':' + gz);
        if (!a) continue;
        for (const i of a) {
          if (this._stamp[i] === t) continue;
          this._stamp[i] = t;
          const v = this.vols[i];
          if (v.x1 < x0 || v.x0 > x1 || v.z1 < z0 || v.z0 > z1 || v.y1 < y0 || v.y0 > y1) continue;
          out.push(v);
        }
      }
    }
    return out;
  }

  // is (x, y, z) inside any volume (the terrain there was cut away)?
  inside(x, y, z) {
    const a = this.map.get(Math.floor(x / this.cell) + ':' + Math.floor(z / this.cell));
    if (a) for (const i of a) if (depthIn(this.vols[i], x, y, z) > 0) return true;
    return false;
  }

  // is (x, z) inside any volume's plan footprint?
  inPlan(x, z) {
    const a = this.map.get(Math.floor(x / this.cell) + ':' + Math.floor(z / this.cell));
    if (a) for (const i of a) if (depthInPlan(this.vols[i], x, z) > 0) return true;
    return false;
  }
}

// ------------------------------------------------------------ the tile
// The volumes that meet a tile's MESH. The lattice sits at (i + 0.5) cell
// (levelArrays), so the mesh spans [ox + cell/2, ox + tm + cell/2]: a box of
// [ox, ox + tm] misses every volume that starts in the half-cell strip past
// the +x / +z edge, terrain the tile alone draws. Padded by CLEAR_M (the
// clip removes the grown volume). ONE query for the bake (bakectx cutNode)
// and the gates.
export function tileVolumes(index, T, ox, oz, y0 = -Infinity, y1 = Infinity) {
  const e = CLEAR_M;
  return index.query(ox + T.xs[0] - e, ox + T.xs[T.g - 1] + e, oz + T.zs[0] - e, oz + T.zs[T.g - 1] + e, y0, y1);
}

// A level's shared template, as plain arrays: index + uv + local x/z are
// pure functions of the level (groundm/bakectx.mjs templateOf shifts them
// onto the L0 sample convention per node level).
export function levelArrays(verts, cell, tileM) {
  const N = 257;
  const stride = levelStride(verts);
  // grid + 1-vert skirt ring around the edge
  const g = verts + 2;
  const count = g * g;
  // just enough to hide LOD seams — huge skirts on coarse tiles read as
  // striped cliff-curtains
  const skirtDrop = Math.min(10, Math.max(2, tileM * 0.02));
  const pos = new Float32Array(g * g * 3);
  const uv = new Float32Array(g * g * 2);
  // per-vertex source sample index into the 257x257 tile, and whether the
  // vertex belongs to the dropped skirt ring — the only two things a tile
  // needs to turn its raw Uint16 heightfield into an aH attribute
  const src = new Int32Array(g * g);
  const skirt = new Uint8Array(g * g);
  let p = 0, u = 0, k = 0;
  let maxR = 0;
  for (let j = 0; j < g; j++) {
    const jj = Math.min(verts - 1, Math.max(0, j - 1));
    const srcJ = strideSrc(jj, stride);
    for (let i = 0; i < g; i++, k++) {
      const ii = Math.min(verts - 1, Math.max(0, i - 1));
      const srcI = strideSrc(ii, stride);
      // sample k at (k+0.5)*cell world convention; LOCAL to the tile — the
      // mesh is positioned at (tx*tile_m, 0, ty*tile_m)
      const lx = (srcI + 0.5) * cell, lz = (srcJ + 0.5) * cell;
      pos[p++] = lx; pos[p++] = 0; pos[p++] = lz;
      uv[u++] = Math.min(1, (srcI + 0.5) / 256);
      uv[u++] = 1 - Math.min(1, (srcJ + 0.5) / 256);
      src[k] = srcJ * N + srcI;
      skirt[k] = (i === 0 || j === 0 || i === g - 1 || j === g - 1) ? 1 : 0;
      const dx = lx - tileM * 0.5, dz = lz - tileM * 0.5;
      const r = dx * dx + dz * dz;
      if (r > maxR) maxR = r;
    }
  }
  const idx = new Uint32Array((g - 1) * (g - 1) * 6);
  let q = 0;
  // the one quad triangulation — game/heightfield.js interpolates it with
  // lodmath triSplit
  for (let j = 0; j < g - 1; j++) {
    for (let i = 0; i < g - 1; i++) q = quadIndex(idx, q, j * g + i, g);
  }
  // the lattice's local x per column / z per row (the cut's quad ranges)
  const xs = new Float64Array(g), zs = new Float64Array(g);
  for (let i = 0; i < g; i++) { xs[i] = pos[3 * i]; zs[i] = pos[3 * i * g + 2]; }
  return { g, count, stride, skirtDrop, pos, uv, src, skirt, idx, xs, zs,
    centerX: tileM * 0.5, centerZ: tileM * 0.5, radiusXZ: Math.sqrt(maxR) };
}

// One tile's heights from its raw Uint16 heightfield: h per template vertex
// (the skirt ring dropped by skirtDrop) and the range of the samples.
export function tileHeights(data, T, zScale, zBase) {
  const n = T.count;
  const src = T.src, skirt = T.skirt;
  const drop = T.skirtDrop;
  const h = new Float32Array(n);
  let y0 = Infinity, y1 = -Infinity;
  for (let i = 0; i < n; i++) {
    const v = data[src[i]] * zScale + zBase;
    if (v < y0) y0 = v;
    if (v > y1) y1 = v;
    h[i] = skirt[i] ? v - drop : v;
  }
  return { h, y0, y1 };
}

// A barycentric polygon { P: flat [l0, l1, l2, ...], S: per side i (vertex
// i -> i+1) the id of the LINE it runs along }: -1 / -2 / -3 the triangle
// side with l0 / l1 / l2 = 0, >= 0 the trace of a volume half-space
// (volume * 1024 + k). Every new vertex is the intersection of two such
// lines, computed ONCE per triangle (the first time a split reaches it, as
// the lerp along that segment — bounded however shallow the angle) and
// cached by the pair: every piece that holds the vertex holds it bit for
// bit, however many subtractions reach it — no near-duplicates, no
// T-junction from rounding.
// split poly by line id (values g at the corners): [outside (f >= 0), inside
// (f <= 0)]; a vertex ON that line (one of its two lines is id) is 0 exactly
let _val = new Float64Array(64);
function splitPoly(poly, id, g0, g1, g2, isect) {
  const P = poly.P, S = poly.S, n = S.length;
  if (_val.length < n) _val = new Float64Array(2 * n);
  const val = _val;
  let pos = 0, neg = 0;
  for (let k = 0; k < n; k++) {
    const f = S[k] === id || S[(k + n - 1) % n] === id ? 0
      : P[3 * k] * g0 + P[3 * k + 1] * g1 + P[3 * k + 2] * g2;
    val[k] = f;
    if (f > 0) pos++; else if (f < 0) neg++;
  }
  if (!neg) return [poly, null];
  if (!pos) return [null, poly];
  const oP = [], oS = [], iP = [], iS = [];
  for (let k = 0; k < n; k++) {
    const m = (k + 1) % n;
    const a = val[k], b = val[m];
    if (a >= 0) { oP.push(P[3 * k], P[3 * k + 1], P[3 * k + 2]); oS.push(a === 0 && b < 0 ? id : S[k]); }
    if (a <= 0) { iP.push(P[3 * k], P[3 * k + 1], P[3 * k + 2]); iS.push(a === 0 && b > 0 ? id : S[k]); }
    if ((a < 0 && b > 0) || (a > 0 && b < 0)) {
      const c = isect(S[k], id, P, k, m, a / (a - b));
      oP.push(c[0], c[1], c[2]); iP.push(c[0], c[1], c[2]);
      oS.push(a > 0 ? id : S[k]);
      iS.push(a < 0 ? id : S[k]);
    }
  }
  return [oS.length >= 3 ? { P: oP, S: oS } : null, iS.length >= 3 ? { P: iP, S: iS } : null];
}

// barycentric area of a polygon (the triangle itself is 0.5)
export function baryArea(poly) {
  const n = poly.length / 3;
  let s = 0;
  for (let k = 0; k < n; k++) {
    const m = (k + 1) % n;
    s += poly[3 * k + 1] * poly[3 * m + 2] - poly[3 * m + 1] * poly[3 * k + 2];
  }
  return Math.abs(s) * 0.5;
}

// The half-space values of volume v at a triangle's three vertices: G[3k + c]
// for half-space k at corner c (H = the corners' heights). The cut removes
// the volume grown by CLEAR_M (game/borevol.js says why 0.5 mm).
function triValues(v, wx, wz, H, G) {
  const P = v.P;
  // a pave cell (game/pave.js cellOf) carries clear 0: its plan polygon is
  // the paved area exactly, never grown
  const cl = v.clear !== undefined ? v.clear : CLEAR_M;
  for (let k = 0; k < v.nk; k++) {
    const A = P[4 * k], B = P[4 * k + 1], C = P[4 * k + 2], D = P[4 * k + 3];
    for (let c = 0; c < 3; c++) {
      const y = C === 0 ? 0 : H[c];
      G[3 * k + c] = A * wx[c] + B * wz[c] + C * y + D - cl;
    }
  }
}

// polygon outside half-space k entirely (every vertex f >= 0)?
function outsideAny(poly, G, nk) {
  const n = poly.length / 3;
  for (let k = 0; k < nk; k++) {
    const g0 = G[3 * k], g1 = G[3 * k + 1], g2 = G[3 * k + 2];
    let all = true;
    for (let q = 0; q < n; q++) {
      if (poly[3 * q] * g0 + poly[3 * q + 1] * g1 + poly[3 * q + 2] * g2 < 0) { all = false; break; }
    }
    if (all) return true;
  }
  return false;
}

const UNIT = [1, 0, 0, 0, 1, 0, 0, 0, 1];
const UNIT_POLY = { P: UNIT, S: [-3, -1, -2] };

// conforming re-tessellation (cutTile pass 2)
// Adjacent volumes of one bore share nearly coplanar walls, so their traces
// in a triangle run ~1e-8 apart and the subtraction leaves sliver pieces
// between them. Vertices closer than 1e-7 (barycentric: L4 13 um, L0 0.05
// um — far under CLEAR_M 0.5 mm) are ONE vertex: the slivers collapse.
const EPS_B = 1e-7;       // a barycentric this small is an exact 0 (on the edge)
const EPS_S = 1e-7;       // two points on one edge closer than this are one
const EPS_M = 1e-6;       // two interior points closer than this are one (L4: 0.18 mm, inside CLEAR_M)
const SLIVER = 1e-9;      // a polygon rest thinner than this (barycentric area) is a sliver
const EPS_X = 1e-12;      // a vertex this close to a chord lies on it
const EPS_T = 1e-11;      // ... strictly between its ends
// the template triangles holding edge p-q: they lie in the (at most four)
// quads around p — quad j (g - 1) + i owns triangles 2q, 2q+1 (levelArrays)
// — no tile-wide edge map (a 400k-entry Map cost ~0.3 s cold, in one piece)
function edgeTrisOf(T, p, q, out) {
  const g = T.g, idx = T.idx, i = p % g, j = (p - i) / g;
  out.length = 0;
  for (let qj = j - 1; qj <= j; qj++) {
    if (qj < 0 || qj > g - 2) continue;
    for (let qi = i - 1; qi <= i; qi++) {
      if (qi < 0 || qi > g - 2) continue;
      const qd = qj * (g - 1) + qi;
      for (let t = 2 * qd; t <= 2 * qd + 1; t++) {
        const a = idx[3 * t], b = idx[3 * t + 1], c = idx[3 * t + 2];
        if ((a === p || b === p || c === p) && (a === q || b === q || c === q)) out.push(t);
      }
    }
  }
  return out;
}
// the original side (zero slot 0..2) a piece edge u -> w runs along, or -1:
// VZ = zero slot of an edge point, -2 a corner (vertex index = its slot),
// -1 interior
function sideOf(zu, u, zw, w) {
  if (zu === -1 || zw === -1) return -1;
  if (zu === -2 && zw === -2) return 3 - u - w;            // corner to corner
  if (zu === -2) return zw !== u ? zw : -1;
  if (zw === -2) return zu !== w ? zu : -1;
  return zu === zw ? zu : -1;
}
// onSeg's result in order along the segment, each vertex once (the line rule
// and the chord test may both find it)
function sortU(ins, sid) {
  ins.sort((a, b) => a[0] - b[0]);
  if (sid < 0 || ins.length < 2) return ins;
  const out = [];
  for (const e of ins) if (!out.some((o) => o[1] === e[1])) out.push(e);
  return out;
}
// ear-clip a convex polygon (V indices, with collinear vertices on its sides)
// into out without Steiner points: clip a strictly convex vertex whose
// removal leaves a polygon of positive area. 0 = clean, 1 = fanned (no
// clean ear), 2 = a sliver rest left out.
function earClip(B, V1, V2, out, clean, eX = EPS_X) {
  const P = B.slice();
  // a spike a -> b -> a (two merged vertices folding one piece's side back
  // on itself: zero area, exactly, in the shoelace sum) and a repeated
  // vertex are dropped: the rest is the piece
  for (let i = 0; P.length >= 3 && i < P.length;) {
    const n = P.length, j = (i + 1) % n;
    if (P[i] === P[j]) { P.splice(j, 1); i = Math.max(0, i - 1); continue; }
    if (P[(i + n - 1) % n] === P[j]) { if (j > i) { P.splice(j, 1); P.splice(i, 1); } else { P.splice(i, 1); P.splice(j, 1); } i = Math.max(0, i - 2); continue; }
    i++;
  }
  if (P.length < 3) return 0;
  const cross = (a, b, c) => (V1[b] - V1[a]) * (V2[c] - V2[a]) - (V1[c] - V1[a]) * (V2[b] - V2[a]);
  let area = 0;
  for (let i = 1; i + 1 < P.length; i++) area += cross(P[0], P[i], P[i + 1]);
  // every piece winds as its triangle (+); one merged vertices folded
  // over (a sliver gone negative) draws nothing
  const sg = 1;
  if (!(area > 0)) return area < 0 ? 2 : 0;
  while (P.length > 3) {
    let best = -1;
    for (let i = 0; i < P.length; i++) {
      const a = P[(i + P.length - 1) % P.length], b = P[i], c = P[(i + 1) % P.length];
      // b off the chord a-c by more than EPS_X (the insertion tolerance: a
      // vertex closer than that IS on the side, never an ear tip), and the
      // remainder not flat
      const e = sg * cross(a, b, c);
      const lc = Math.hypot(V1[c] - V1[a], V2[c] - V2[a]);
      if (e > 2 * eX * lc && area - e > eX * lc) { best = i; break; }
    }
    if (best < 0 && clean) {
      // second chance (a rest bent by merged vertices, not convex): any
      // positive ear holding no other vertex, its diagonal on no vertex
      for (let i = 0; i < P.length && best < 0; i++) {
        const a = P[(i + P.length - 1) % P.length], b = P[i], c = P[(i + 1) % P.length];
        const e = sg * cross(a, b, c);
        if (!(e > 0) || !(area - e > 0)) continue;
        let hit = false;
        for (const x of P) {
          if (x === a || x === b || x === c) continue;
          if (sg * cross(a, b, x) >= 0 && sg * cross(b, c, x) >= 0 && sg * cross(c, a, x) >= 0) { hit = true; break; }
        }
        if (!hit && clean(a, c)) best = i;
      }
    }
    if (best < 0) {
      // no clean ear: the rest is a sliver between nearly collinear vertices
      // (two volumes' nearly coplanar walls) — under SLIVER, or thinner than EPS_B all
      // along (twice its area over its extent: the width at which two
      // vertices are one), it is not drawn (a gap far below a float32 ulp,
      // never a T-junction); else fanned and counted (the gate's T-junction
      // test judges it)
      let ext = 0;
      for (let i = 1; i < P.length; i++) ext = Math.max(ext, Math.hypot(V1[P[i]] - V1[P[0]], V2[P[i]] - V2[P[0]]));
      if (area <= SLIVER || area <= EPS_B * ext) return 2;
      if (globalThis.__dbg) console.log('FALLBACK', area, JSON.stringify(P.map((i) => [i, V1[i], V2[i]])));
      for (let i = 1; i + 1 < P.length; i++) if (sg * cross(P[0], P[i], P[i + 1]) > 0) out.push(P[0], P[i], P[i + 1]);
      return 1;
    }
    const a = P[(best + P.length - 1) % P.length], b = P[best], c = P[(best + 1) % P.length];
    area -= sg * cross(a, b, c);
    out.push(a, b, c);
    P.splice(best, 1);
  }
  if (sg * cross(P[0], P[1], P[2]) > 0) out.push(P[0], P[1], P[2]);
  return 0;
}

// PAVED GROUND IS RE-MESHED BY THE SAME CLIPPER (WAYSG2 P3). `pave` =
// { cells, at(kind, wx, wz) -> y | NaN }: cells are game/pave.js cells (the
// convex plan polygons of the paved areas in the volume format: vertical
// half-spaces only, clear 0, kind 1 road / 2 walk, walk cells first) and
// `at` their surface height (game/pave.js roadY / walkTopAt within a mm).
// A cell is a volume whose inside is KEPT and labelled instead of dropped:
// per triangle the bores subtract first, then each cell in index order
// claims the part of the rest inside it (the first claim wins: a walk over
// the road piece it lies on). The cell lines are cut lines of the mesh, so
// the formation edges and the kerb lines are straight mesh edges. Then
// (paveFinish) every vertex of a labelled piece takes the paved height —
// the territory's vertex on a formation edge too (it is the same vertex:
// the territory meets the edge height, nothing past its first grid vertex
// moves) — and where a road vertex meets a walk (the kerb line) the road
// side gets its own copy at road level and the two are stitched by a
// vertical wall: the kerb face, h_m high, on the kerb line exactly. L0
// tiles only (bakectx cutNode hands pave cells at node level 0); skirt
// triangles are never paved. The labels index the per-triangle output
// lists. A PLATFORM (game/pave.js platforms: a railway platform, the ground
// under a station canopy) is its plane; on its track-facing sides (pave.edge)
// the platform side gets a copy at the plane and the ground keeps the bed
// (its support nodes outside every ring), a vertical wall between.
const PAVE_ROAD = 1, PAVE_WALK = 2, PAVE_PLAT = 3, PAVE_FLIGHT = 4;
export { PAVE_ROAD, PAVE_WALK, PAVE_PLAT };

// ONE LINE, ONE ID. Pave cells tile the paved area edge to edge (no CLEAR_M
// growth: neighbouring section quads share a side, a walk's outer edge lies
// on its road's formation edge), so two cells often bound along the SAME
// geometric line through two half-spaces that differ in the last bits; as
// two line ids their "intersection" is ill-conditioned and lands anywhere
// along the line (a T-junction, a sliver). Per tile every cell half-space is
// snapped to a canonical line: lines whose direction agrees within
// SNAP_ANG rad and whose offset at the tile centre agrees within SNAP_OFF m
// are one line — one id, coefficients exactly equal or exactly negated (the
// two sides' values are then exact opposites: splitPoly's on-the-line 0
// holds for both). The move is under 0.1 mm anywhere on a tile.
const SNAP_ANG = 1e-6, SNAP_OFF = 1e-5;
function snapCellLines(cells, cx, cz, idBase) {
  const canon = [];                 // [A, B, D] per line id (direction in [0, pi))
  const hash = new Map();
  const QA = SNAP_ANG, QO = SNAP_OFF;
  const out = new Array(cells.length);
  for (let i = 0; i < cells.length; i++) {
    const c = cells[i], nk = c.nk, P = new Float64Array(4 * nk), lid = new Int32Array(nk);
    for (let k = 0; k < nk; k++) {
      let A = c.P[4 * k], B = c.P[4 * k + 1], D = c.P[4 * k + 3];
      let th = Math.atan2(B, A), s = 1;
      if (th < 0) { th += Math.PI; s = -1; }
      if (th >= Math.PI) th -= Math.PI;
      const d = s * (A * cx + B * cz + D);
      // a canonical line within the snap of (th, d); a direction near 0 is
      // also met as one near pi the other way round (angle - pi, offset -d:
      // the negated half-space)
      const find = (tth, td) => {
        const ka = Math.round(tth / QA), ko = Math.round(td / QO);
        for (let da = -1; da <= 1; da++) {
          for (let dd = -1; dd <= 1; dd++) {
            const lst = hash.get((ka + da) + ':' + (ko + dd));
            if (lst) for (const L of lst) if (Math.abs(tth - canon[L][3]) <= QA && Math.abs(td - canon[L][4]) <= QO) return L;
          }
        }
        return -1;
      };
      let id = find(th, d), sg = s;
      if (id < 0 && th > Math.PI - 2 * QA) { id = find(th - Math.PI, -d); sg = -s; }
      if (id < 0 && th < 2 * QA) { id = find(th + Math.PI, -d); sg = -s; }
      if (id < 0) {
        id = canon.length;
        canon.push([s * A, s * B, s * D, th, d]);
        const key = Math.round(th / QA) + ':' + Math.round(d / QO);
        let lst = hash.get(key);
        if (!lst) hash.set(key, lst = []);
        lst.push(id);
        sg = s;
      }
      const L = canon[id];
      // sg: this half-space = sg * the canonical one
      P[4 * k] = sg * L[0]; P[4 * k + 1] = sg * L[1]; P[4 * k + 2] = 0; P[4 * k + 3] = sg * L[2];
      lid[k] = idBase + id;
    }
    out[i] = { ...c, P, lid };
  }
  return { cells: out, lines: canon.length };
}

// Cut one tile. T = levelArrays, h = its heights per template vertex,
// (ox, oz) = the tile's world origin, bores = the volumes meeting it
// (tileVolumes), pave = the tile's pave cells (game/pave.js tilePave) or
// null. Returns null when no triangle meets a volume or a cell (keep the
// template), else own arrays: { pos, uv, h, idx, cut, stats[, pave] } —
// vertices [0, T.count) are the template's own, byte for byte; new cut
// vertices follow.
export function cutTile(T, h, ox, oz, bores, pave = null) {
  const nB = bores ? bores.length : 0;
  let cells = pave && pave.cells && pave.cells.length ? pave.cells : null;
  if (!nB && !cells) return null;
  // the cells' lines snapped (their ids follow the bores' vol * 1024 + k)
  let nLines = 0;
  if (cells) {
    if (cells.every((c) => c.gid)) {
      // shared line ids from the index (game/pave.js shareLines): made
      // compact for this tile
      const loc = new Map();
      cells = cells.map((c) => {
        const lid = new Int32Array(c.nk);
        for (let k = 0; k < c.nk; k++) {
          let id = loc.get(c.gid[k]);
          if (id === undefined) loc.set(c.gid[k], id = loc.size);
          lid[k] = nB * 1024 + id;
        }
        return { ...c, lid };
      });
      nLines = loc.size;
    } else {
      const sn = snapCellLines(cells, ox + T.centerX, oz + T.centerZ, nB * 1024);
      cells = sn.cells; nLines = sn.lines;
    }
    if (nB * 1024 + nLines >= 67108860) throw new Error('borecut: too many lines on one tile');
  }
  const vols = cells ? (nB ? bores.concat(cells) : cells) : bores;
  // A pave tile's cell lines meet at shallow angles (a curved kerb's
  // nearly collinear sides, a road line passing a walk corner by tens of
  // um): their intersections are exact only to ~1e-13 barycentric off the
  // OTHER line, so the chord test runs at 1e-9 (0.5 nm at L0), a crossing
  // within 1e-9 of a template edge is that edge's point (eZ), and interior
  // points within EPS_M are one. Coincidence beyond that is by IDENTITY (the
  // shared line ids and THE LINE RULE in pass 2), never by distance. A
  // bore-only tile keeps the original tolerances (its output byte for byte).
  const eX = cells ? 1e-9 : EPS_X, eT = cells ? 1e-9 : EPS_T, eM = EPS_M;
  const eB = EPS_B, eS = EPS_S, eZ = cells ? 1e-9 : 0;
  const g = T.g, pos = T.pos, idx = T.idx, nTri = idx.length / 3;
  // candidate (triangle, volume) pairs over the lattice: a volume's AABB
  // covers a block of quads; quad q = j (g - 1) + i owns triangles 2q, 2q+1
  const head = new Int32Array(nTri).fill(-1);
  const nxt = [], vid = [], cand = [];
  const skirt = T.skirt;
  for (let vi = 0; vi < vols.length; vi++) {
    const v = vols[vi], isCell = vi >= nB;
    const X0 = v.x0 - ox, X1 = v.x1 - ox, Z0 = v.z0 - oz, Z1 = v.z1 - oz;
    let i0 = 0, i1 = g - 2, j0 = 0, j1 = g - 2;
    while (i0 <= i1 && T.xs[i0 + 1] < X0) i0++;
    while (i1 >= i0 && T.xs[i1] > X1) i1--;
    while (j0 <= j1 && T.zs[j0 + 1] < Z0) j0++;
    while (j1 >= j0 && T.zs[j1] > Z1) j1--;
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const q = j * (g - 1) + i;
        for (let t = 2 * q; t <= 2 * q + 1; t++) {
          const a = idx[3 * t], b = idx[3 * t + 1], c = idx[3 * t + 2];
          if (isCell) { if (skirt[a] || skirt[b] || skirt[c]) continue; }
          else {
            const ylo = Math.min(h[a], h[b], h[c]), yhi = Math.max(h[a], h[b], h[c]);
            if (yhi < v.y0 || ylo > v.y1) continue;
          }
          if (head[t] < 0) cand.push(t);
          nxt.push(head[t]); vid.push(vi); head[t] = nxt.length - 1;
        }
      }
    }
  }
  if (!cand.length) return null;
  const candS = Int32Array.from(cand).sort();     // native numeric sort
  // pass 1: the pieces of every candidate triangle
  const cutT = [], cutP = [];        // cut triangles (ascending) and their pieces
  const cutL = cells ? [] : null;    // per cut triangle: per piece its label (0 ground, PAVE_ROAD, PAVE_WALK, PAVE_PLAT, PAVE_FLIGHT)
  const chain = [];
  const wx = new Float64Array(3), wz = new Float64Array(3), H = new Float64Array(3);
  let G = new Float64Array(96);
  let trisDropped = 0;
  for (const t of candS) {
    for (let c = 0; c < 3; c++) {
      const k = idx[3 * t + c];
      wx[c] = ox + pos[3 * k]; wz[c] = oz + pos[3 * k + 2];
      H[c] = h[k];
    }
    let pieces = null;            // null = still the whole triangle
    const lab = cells ? [] : null, labK = cells ? [] : null;   // the paved pieces and their kinds
    // the lines of this triangle and their pairwise intersections (once;
    // a line id is volume * 1024 + k, volume < 65536)
    const xs = new Map();
    const isect = (p, q, P, k, m, tt) => {
      const key = p < q ? (p + 4) * 67108864 + (q + 4) : (q + 4) * 67108864 + (p + 4);
      let r = xs.get(key);
      if (r) return r;
      r = [P[3 * k] + (P[3 * m] - P[3 * k]) * tt, P[3 * k + 1] + (P[3 * m + 1] - P[3 * k + 1]) * tt,
        P[3 * k + 2] + (P[3 * m + 2] - P[3 * k + 2]) * tt];
      // on a triangle side: that barycentric is exactly 0
      if (p < 0 || q < 0) r[-(p < 0 ? p : q) - 1] = 0;
      xs.set(key, r);
      return r;
    };
    // the volumes on this triangle: bores in the chain's order (as ever);
    // with pave cells in ascending index (bores, then walk, then road cells:
    // the claim order)
    chain.length = 0;
    for (let e = head[t]; e >= 0; e = nxt[e]) chain.push(e);
    if (cells) chain.sort((a, b) => vid[a] - vid[b]);
    for (const e of chain) {
      const v = vols[vid[e]], isCell = vid[e] >= nB;
      if (G.length < 3 * v.nk) G = new Float64Array(6 * v.nk);
      triValues(v, wx, wz, H, G);
      if (outsideAny(UNIT, G, v.nk)) continue;
      const lb = vid[e] * 1024;        // line ids: a volume holds < 1024 half-spaces
      if (v.nk >= 1024) throw new Error('borecut: a volume with >= 1024 half-spaces');
      if (!isCell && vid[e] >= 65536) throw new Error('borecut: >= 65536 volumes on one tile');
      const LID = isCell ? v.lid : null;   // a cell's lines: their snapped ids
      // the triangle's part inside v (an intersection: no fragments); none
      // -> v misses it. Its barycentric box rejects far pieces cheaply.
      let Ip = UNIT_POLY;
      for (let k = 0; k < v.nk && Ip; k++) Ip = splitPoly(Ip, LID ? LID[k] : lb + k, G[3 * k], G[3 * k + 1], G[3 * k + 2], isect)[1];
      if (!Ip || baryArea(Ip.P) <= MIN_BARY_AREA) continue;
      const I = Ip.P;
      let a1 = Infinity, A1 = -Infinity, a2 = Infinity, A2 = -Infinity;
      for (let q = 0; q < I.length; q += 3) {
        if (I[q + 1] < a1) a1 = I[q + 1]; if (I[q + 1] > A1) A1 = I[q + 1];
        if (I[q + 2] < a2) a2 = I[q + 2]; if (I[q + 2] > A2) A2 = I[q + 2];
      }
      const src = pieces || [UNIT_POLY];
      const out = [];
      for (const pl of src) {
        const poly = pl.P;
        let b1 = Infinity, B1 = -Infinity, b2 = Infinity, B2 = -Infinity;
        for (let q = 0; q < poly.length; q += 3) {
          if (poly[q + 1] < b1) b1 = poly[q + 1]; if (poly[q + 1] > B1) B1 = poly[q + 1];
          if (poly[q + 2] < b2) b2 = poly[q + 2]; if (poly[q + 2] > B2) B2 = poly[q + 2];
        }
        if (B1 < a1 || b1 > A1 || B2 < a2 || b2 > A2 || outsideAny(poly, G, v.nk)) { out.push(pl); continue; }
        // subtract: what lies outside half-space k (and inside the ones
        // before it) is kept; what is inside all of them is the bore
        let rest = pl;
        for (let k = 0; k < v.nk && rest; k++) {
          const [o, r] = splitPoly(rest, LID ? LID[k] : lb + k, G[3 * k], G[3 * k + 1], G[3 * k + 2], isect);
          if (o && baryArea(o.P) > MIN_BARY_AREA) out.push(o);
          rest = r;
        }
        if (isCell && rest && baryArea(rest.P) > MIN_BARY_AREA) { lab.push(rest); labK.push(v.kind === 3 ? PAVE_FLIGHT : v.kind === 4 ? PAVE_PLAT : v.kind); }
      }
      pieces = out;
    }
    if (!pieces) continue;
    if (!pieces.length && !(lab && lab.length)) trisDropped++;
    cutT.push(t);
    if (cells) {
      // the ground pieces, then the paved ones (labels parallel)
      // (each piece's side lines ride along: pass 2's line rule)
      const wS = (pl) => { pl.P.S = pl.S; return pl.P; };
      cutP.push(pieces.map(wS).concat(lab.map(wS)));
      const L = new Uint8Array(pieces.length + lab.length);
      for (let q = 0; q < labK.length; q++) L[pieces.length + q] = labK[q];
      cutL.push(L);
    } else cutP.push(pieces.map((pl) => pl.P));
  }
  if (!cutT.length) return null;
  // pass 2: A CUT EDGE IS SHARED. Every piece vertex on an original edge is
  // snapped to that edge (exact 0 barycentric) at a canonical parameter s
  // (from the lower template index toward the higher, merged across the two
  // triangles of the edge); every triangle holding such an edge — cut or not,
  // terrain or skirt — is re-tessellated through ALL points on its edges, and
  // inside a cut triangle every piece through all its siblings' vertices on
  // its sides. Ear clipping without Steiner points: same plane, same
  // surface, no T-junction anywhere in the tile (the gate counts them).
  const reg = new Map();             // edge key -> parameters s
  const ekey = (p, q) => (p < q ? p * 131072 + q : q * 131072 + p);
  for (let f = 0; f < cutT.length; f++) {
    const t = cutT[f];
    for (const poly of cutP[f]) {
      for (let q = 0; q < poly.length; q += 3) {
        for (let c = 0; c < 3; c++) if (poly[q + c] < eB) poly[q + c] = 0;
        const z0 = poly[q] === 0, z1 = poly[q + 1] === 0, z2 = poly[q + 2] === 0;
        if (z0 + z1 + z2 !== 1) continue;
        const ci = z0 ? 1 : 0, cj = z2 ? 1 : 2;           // the two live corners
        const p = idx[3 * t + ci], r = idx[3 * t + cj];
        const lp = poly[q + ci], lr = poly[q + cj];
        const sv = p < r ? lr / (lp + lr) : lp / (lp + lr);
        const k = ekey(p, r);
        let a = reg.get(k); if (!a) reg.set(k, a = []); a.push(sv);
      }
    }
  }
  for (const [k, a] of reg) {
    a.sort((x, y) => x - y);
    const m = [];
    for (const v of a) {
      if (v <= eS || v >= 1 - eS) continue;         // a corner
      if (!m.length || v - m[m.length - 1] > eS) m.push(v);
    }
    if (m.length) reg.set(k, m); else reg.delete(k);
  }
  const canon = (k, v) => {
    const a = reg.get(k);
    if (!a || v <= eS || v >= 1 - eS) return v <= 0.5 ? 0 : 1;
    let lo = 0, hi = a.length - 1;
    while (lo < hi) { const md = (lo + hi) >> 1; if (a[md] < v) lo = md + 1; else hi = md; }
    let b = a[lo];
    if (lo > 0 && Math.abs(a[lo - 1] - v) < Math.abs(b - v)) b = a[lo - 1];
    return b;
  };
  // the triangles to re-tessellate: the cut ones and every triangle on an
  // edge that now carries points
  const isCut = new Map();
  for (let f = 0; f < cutT.length; f++) isCut.set(cutT[f], f);
  const redo = new Set(cutT), etBuf = [];
  for (const k of reg.keys()) for (const t of edgeTrisOf(T, Math.floor(k / 131072), k % 131072, etBuf)) redo.add(t);
  const entries = Array.from(Int32Array.from(redo).sort());
  // new vertices: edge points shared tile-wide, interior ones per triangle
  const vTri = [], vBary = [], edgeV = new Map();
  const n0 = T.count;
  const entryTris = [];               // per entry: flat global vertex triples
  const entryLabN = cells ? [] : null; // per entry: its ground / road / walk / flight / platform triangle counts (in that order)
  const plab = [];                    // per entry scratch: per piece its output list (its label: 0 ground, PAVE_ROAD, PAVE_WALK, PAVE_PLAT, PAVE_FLIGHT)
  // (pave cells) per piece per side its LINE id, and per vertex the lines
  // it lies on: a vertex of one piece that lies on another piece's side line
  // is inserted there by identity, never by distance (THE LINE RULE below)
  const pside = [];
  const VL = new Map();
  let trisSplit = 0, outTris = nTri - entries.length, earFallback = 0, slivers = 0;
  const V1 = [], V2 = [], VK = [], VZ = [];   // per entry scratch: l1, l2, global id, zero slot
  for (const t of entries) {
    const f = isCut.get(t);
    const cn = [idx[3 * t], idx[3 * t + 1], idx[3 * t + 2]];
    V1.length = V2.length = VK.length = VZ.length = 0;
    // corners
    V1.push(0, 1, 0); V2.push(0, 0, 1); VK.push(cn[0], cn[1], cn[2]); VZ.push(-2, -2, -2);
    const edgeIds = [new Map(), new Map(), new Map()];   // per side (its zero slot): s -> V index
    const addEdge = (ci, cj, sv) => {   // point on edge (corner ci, corner cj) at s
      const p = cn[ci], r = cn[cj], k = ekey(p, r), side = edgeIds[3 - ci - cj];
      const hit = side.get(sv);
      if (hit !== undefined) return hit;
      // its barycentrics in THIS triangle: l_lo = 1 - s, l_hi = s
      const l = [0, 0, 0];
      const lHi = sv, lLo = 1 - sv;
      if (p < r) { l[ci] = lLo; l[cj] = lHi; } else { l[ci] = lHi; l[cj] = lLo; }
      let em = edgeV.get(k);
      if (!em) edgeV.set(k, em = new Map());
      let g = em.get(sv);
      if (g === undefined) {
        g = n0 + vTri.length; em.set(sv, g);
        vTri.push(t); vBary.push(l[0], l[1], l[2]);
      }
      V1.push(l[1]); V2.push(l[2]); VK.push(g); VZ.push(3 - ci - cj);
      side.set(sv, V1.length - 1);
      return V1.length - 1;
    };
    for (const [ci, cj] of [[0, 1], [1, 2], [2, 0]]) {
      const a = reg.get(ekey(cn[ci], cn[cj]));
      if (a) for (const sv of a) addEdge(ci, cj, sv);
    }
    // the pieces as V index lists
    let polys;
    plab.length = 0; pside.length = 0; VL.clear();
    if (f === undefined) { polys = [[0, 1, 2]]; plab.push(0); trisSplit++; }
    else {
      polys = [];
      // this triangle's interior vertices, hashed on an EPS_M grid
      const cellOf = new Map();
      const qk = (a, b) => a * 2097152 + b;
      const src = cutP[f];
      for (let pi = 0; pi < src.length; pi++) {
        const poly = src[pi];
        const L = [], Ls = [], Lv = [], PS = cells && poly.S ? poly.S : null, nq = poly.length / 3;
        for (let q = 0; q < poly.length; q += 3) {
          const l0 = poly[q], l1 = poly[q + 1], l2 = poly[q + 2];
          // (pave cells: a crossing of two cell lines that lies ON a template
          // edge — a walk corner on a road corner, both on a diagonal — comes
          // out of the intersection a rounding off it; within eZ it IS the
          // edge's point, else the neighbour triangle holds it as an edge
          // point and this one as an interior twin, two vertices at one xz)
          const z0 = l0 <= eZ, z1 = l1 <= eZ, z2 = l2 <= eZ, nz = z0 + z1 + z2;
          let vi;
          if (nz >= 2) vi = !z0 ? 0 : !z1 ? 1 : 2;
          else if (nz === 1) {
            const ci = z0 ? 1 : 0, cj = z2 ? 1 : 2;
            const p = cn[ci], r = cn[cj];
            const lpv = [l0, l1, l2][ci], lrv = [l0, l1, l2][cj];
            const sv = canon(ekey(p, r), p < r ? lrv / (lpv + lrv) : lpv / (lpv + lrv));
            // s runs from the lower template index: 0 is that corner
            if (sv === 0 || sv === 1) vi = (sv === 0) === (p < r) ? ci : cj;
            else vi = addEdge(ci, cj, sv);
          } else {
            vi = -1;
            const qa = Math.floor(l1 / eM), qb = Math.floor(l2 / eM);
            for (let da = -1; da <= 1 && vi < 0; da++) {
              for (let db = -1; db <= 1 && vi < 0; db++) {
                const lst = cellOf.get(qk(qa + da, qb + db));
                if (lst) for (const u of lst) if (Math.abs(V1[u] - l1) < eM && Math.abs(V2[u] - l2) < eM) { vi = u; break; }
              }
            }
            if (vi < 0) {
              vi = V1.length;
              const ck = qk(qa, qb), lst = cellOf.get(ck);
              if (lst) lst.push(vi); else cellOf.set(ck, [vi]);
              V1.push(l1); V2.push(l2); VZ.push(-1);
              VK.push(n0 + vTri.length); vTri.push(t); vBary.push(l0, l1, l2);
            }
          }
          if (L[L.length - 1] !== vi) { L.push(vi); if (PS) Ls.push(PS[q / 3]); }
          else if (PS) Ls[Ls.length - 1] = PS[q / 3];
          if (PS) Lv.push(vi, PS[(q / 3 + nq - 1) % nq], PS[q / 3]);
        }
        while (L.length > 1 && L[0] === L[L.length - 1]) { L.pop(); if (PS) Ls.pop(); }
        if (L.length >= 3) {
          polys.push(L); pside.push(PS ? Ls : null); plab.push(cutL ? cutL[f][pi] : 0);
          // the lines each kept vertex lies on (a dropped, degenerate piece
          // claims nothing)
          for (let e = 0; e < Lv.length; e += 3) {
            let m = VL.get(Lv[e]);
            if (!m) VL.set(Lv[e], m = []);
            if (Lv[e + 1] >= 0 && !m.includes(Lv[e + 1])) m.push(Lv[e + 1]);
            if (Lv[e + 2] >= 0 && !m.includes(Lv[e + 2])) m.push(Lv[e + 2]);
          }
        }
      }
    }
    // the vertices of this triangle strictly inside segment u -> w, in order
    // (on a shared original side: the side's own points; else the chord
    // test). ONE predicate for the piece sides and the repair below — the
    // gate's T-junction test is the same.
    // (a large vertex set — a coarse triangle crossed by a whole bore — is
    // bucketed on a grid over the barycentric square)
    const nV = V1.length;
    const GN = nV > 48 ? Math.min(64, Math.ceil(Math.sqrt(nV))) : 0;
    let gHead = null, gNext = null;
    if (GN) {
      gHead = new Int32Array(GN * GN).fill(-1); gNext = new Int32Array(nV);
      for (let x = 0; x < nV; x++) {
        const cx = Math.min(GN - 1, Math.max(0, Math.floor(V1[x] * GN))), cy = Math.min(GN - 1, Math.max(0, Math.floor(V2[x] * GN)));
        gNext[x] = gHead[cy * GN + cx]; gHead[cy * GN + cx] = x;
      }
    }
    const onSeg = (u, w, sid = -1) => {
      const ins = [];
      const dx = V1[w] - V1[u], dy = V2[w] - V2[u], len2 = dx * dx + dy * dy;
      if (!(len2 > 0)) return ins;
      // THE LINE RULE (pave cells): a side running along line sid holds
      // every vertex that lies on sid (an intersection of sid with another
      // line, possibly merged with a near twin) strictly between its ends —
      // by identity, so two pieces along one line hold the same points
      if (sid >= 0) {
        for (const [x, m] of VL) {
          if (x === u || x === w || !m.includes(sid)) continue;
          const tt = ((V1[x] - V1[u]) * dx + (V2[x] - V2[u]) * dy) / len2;
          if (tt > eT && tt < 1 - eT) ins.push([tt, x]);
        }
      }
      const zs = sideOf(VZ[u], u, VZ[w], w);
      const test = (x) => {
        if (x === u || x === w) return;
        if (zs >= 0) { if (!(VZ[x] === zs || (x < 3 && x !== zs))) return; }
        else if (Math.abs(dx * (V2[x] - V2[u]) - dy * (V1[x] - V1[u])) > eX * Math.sqrt(len2)) return;
        const tt = ((V1[x] - V1[u]) * dx + (V2[x] - V2[u]) * dy) / len2;
        if (tt > eT && tt < 1 - eT) ins.push([tt, x]);
      };
      if (GN) {
        const m = 1e-9;
        const x0 = Math.max(0, Math.floor((Math.min(V1[u], V1[w]) - m) * GN)), x1 = Math.min(GN - 1, Math.floor((Math.max(V1[u], V1[w]) + m) * GN));
        const y0 = Math.max(0, Math.floor((Math.min(V2[u], V2[w]) - m) * GN)), y1 = Math.min(GN - 1, Math.floor((Math.max(V2[u], V2[w]) + m) * GN));
        for (let cy = y0; cy <= y1; cy++) for (let cx = x0; cx <= x1; cx++) for (let x = gHead[cy * GN + cx]; x >= 0; x = gNext[x]) test(x);
        return sortU(ins, sid);
      }
      for (let x = 0; x < nV; x++) {
        if (x === u || x === w) continue;
        if (zs >= 0) { if (!(VZ[x] === zs || (x < 3 && x !== zs))) continue; }
        else if (Math.abs(dx * (V2[x] - V2[u]) - dy * (V1[x] - V1[u])) > eX * Math.sqrt(len2)) continue;
        const tt = ((V1[x] - V1[u]) * dx + (V2[x] - V2[u]) * dy) / len2;
        if (tt > eT && tt < 1 - eT) ins.push([tt, x]);
      }
      return sortU(ins, sid);
    };
    const clean = (u, w) => onSeg(u, w).length === 0;
    const ring = (P, Ls = null) => {
      const B = [];
      for (let e = 0; e < P.length; e++) {
        B.push(P[e]);
        for (const [, x] of onSeg(P[e], P[(e + 1) % P.length], Ls ? Ls[e] : -1)) B.push(x);
      }
      return B;
    };
    // each piece through every vertex on its sides, then ear clipping (with
    // pave cells: one triangle list per label — ground, road, walk,
    // platform, flight — over one vertex set)
    const outs = cells ? [[], [], [], [], []] : [[]];
    for (let pi = 0; pi < polys.length; pi++) {
      const P = polys[pi];
      const ec = earClip(ring(P, cells ? pside[pi] : null), V1, V2, outs[plab[pi]], clean, eX);
      if (ec === 1) earFallback++; else if (ec === 2) slivers++;
    }
    // repair: a triangle whose edge still holds a vertex (an ear diagonal
    // past a nearly collinear vertex, a sliver left out) is re-clipped
    // through it, until no edge holds one
    const lists = [];
    for (const out0 of outs) {
      let done = [], todo = out0;
      for (let it = 0; it < 6 && todo.length; it++) {
        const nt = [];
        for (let q = 0; q < todo.length; q += 3) {
          const tri = [todo[q], todo[q + 1], todo[q + 2]];
          const B = ring(tri);
          if (B.length === 3) { done.push(tri[0], tri[1], tri[2]); continue; }
          const ec = earClip(B, V1, V2, nt, clean, eX);
          if (ec === 1) earFallback++; else if (ec === 2) slivers++;
        }
        todo = nt;
      }
      const out = todo.length ? done.concat(todo) : done;
      const tl = [];
      for (const vi of out) tl.push(VK[vi]);
      lists.push(tl);
    }
    if (cells) {
      entryTris.push(lists[0].concat(lists[1], lists[2], lists[4], lists[3]));
      entryLabN.push([lists[0].length / 3, lists[1].length / 3, lists[2].length / 3, lists[4].length / 3, lists[3].length / 3]);
      outTris += (lists[0].length + lists[1].length + lists[2].length + lists[4].length + lists[3].length) / 3;
      continue;
    }
    entryTris.push(lists[0]);
    outTris += lists[0].length / 3;
  }
  const newV = vTri.length, nV = n0 + newV;
  const oPos = new Float32Array(nV * 3), oUv = new Float32Array(nV * 2), oH = new Float32Array(nV);
  oPos.set(pos); oUv.set(T.uv); oH.set(h);
  const uv = T.uv;
  for (let n = 0; n < newV; n++) {
    const t = vTri[n], k = n0 + n;
    const a = idx[3 * t], b = idx[3 * t + 1], c = idx[3 * t + 2];
    const l0 = vBary[3 * n], l1 = vBary[3 * n + 1], l2 = vBary[3 * n + 2];
    // an edge point: the canonical lerp (lower index -> higher), so the two
    // triangles of the edge compute bit-identical attributes
    let A = a, Bv = b, C = c, m0 = l0, m1 = l1, m2 = l2;
    if (l0 === 0 || l1 === 0 || l2 === 0) {
      const ci = l0 === 0 ? 1 : 0, cj = l2 === 0 ? 1 : 2;
      const p = [a, b, c][ci], r = [a, b, c][cj];
      const lo = Math.min(p, r), hi = Math.max(p, r), sv = (p < r ? [l0, l1, l2][cj] : [l0, l1, l2][ci]);
      A = lo; Bv = hi; C = lo; m0 = 1 - sv; m1 = sv; m2 = 0;
    }
    oPos[3 * k] = m0 * pos[3 * A] + m1 * pos[3 * Bv] + m2 * pos[3 * C];
    oPos[3 * k + 2] = m0 * pos[3 * A + 2] + m1 * pos[3 * Bv + 2] + m2 * pos[3 * C + 2];
    oUv[2 * k] = m0 * uv[2 * A] + m1 * uv[2 * Bv] + m2 * uv[2 * C];
    oUv[2 * k + 1] = m0 * uv[2 * A + 1] + m1 * uv[2 * Bv + 1] + m2 * uv[2 * C + 1];
    oH[k] = m0 * h[A] + m1 * h[Bv] + m2 * h[C];
  }
  const oIdx = new Uint32Array(outTris * 3);
  let w = 0, from = 0;
  for (let f = 0; f < entries.length; f++) {
    const t = entries[f];
    // the untouched run before it, verbatim
    oIdx.set(idx.subarray(3 * from, 3 * t), w); w += 3 * (t - from); from = t + 1;
    oIdx.set(entryTris[f], w); w += entryTris[f].length;
  }
  oIdx.set(idx.subarray(3 * from), w);
  const res = { pos: oPos, uv: oUv, h: oH, idx: oIdx,
    // provenance for the gate: the re-tessellated triangles (ascending),
    // which of them the bore cut (the rest only split along a shared cut
    // edge), their output triangle counts, and per new vertex its source
    // triangle and barycentrics there
    cut: { tris: entries, cutBy: entries.map((t) => isCut.has(t)), nOut: entryTris.map((a) => a.length / 3),
      vTri: Int32Array.from(vTri), vBary: Float64Array.from(vBary) },
    stats: { trisCut: cutT.length, trisSplit, trisDropped, trisOut: outTris, newVerts: newV, earFallback, slivers,
      bytes: oPos.byteLength + oUv.byteLength + oH.byteLength + oIdx.byteLength } };
  if (cells) paveFinish(res, T, ox, oz, pave, nTri, entryLabN, h, nB ? bores : null);
  return res;
}

// THE WELD (pave tiles, before the heights). One crossing of two cell lines
// can come out of two template triangles (or two line pairs) as two new
// vertices a float32 ulp apart (~1e-5 m at L0): the pieces on either side
// then end on different twins and leave a sliver of no width — an overlap
// (a directed edge held twice) or a hole (an open edge). New vertices of the
// kept mesh within WELD_M of each other in plan are one (the lowest index);
// the triangles that collapse (two corners one vertex) are dropped, the
// entries' counts (cut.nOut, entryLabN) follow. A new vertex within
// WELD_NODE_M of a lattice node (a constraint line crossing a lattice edge a
// hair from its end) is that node: else the node stays ground at the
// template while its twin is paved, a near-vertical sliver face between
// them. Template vertices never move: a template triangle never collapses.
// Then the sliver clean (below).
const WELD_M = 5e-5, WELD_NODE_M = 2e-4;
function paveWeld(res, T, entryLabN, greedy = false) {
  const cut = res.cut, oPos = res.pos, outTris = res.idx.length / 3, n0 = T.count;
  let oIdx = res.idx;
  const nV = res.h.length, rep = new Int32Array(nV);
  for (let v = 0; v < nV; v++) rep[v] = v;
  const used = new Uint8Array(nV);
  for (let k = 0; k < outTris * 3; k++) used[oIdx[k]] = 1;
  const G = new Map(), key = (i, j) => (i + 1048576) * 4194304 + (j + 1048576);
  // a new vertex inside a skirt face (weight on a skirt corner): the skirt
  // hangs vertical under the tile border, so its points meet the surface's
  // in plan at other heights — a weld across the two puts a surface corner
  // down the skirt (a kerb step at the border: 56_17 at 7202.51, 2176.25).
  // Skirt points weld only to skirt points, never to a lattice node
  const skirtF = (v) => {
    const n = v - n0, t = cut.vTri[n];
    let w = 0;
    for (let c = 0; c < 3; c++) if (T.skirt[T.idx[3 * t + c]]) w += cut.vBary[3 * n + c];
    return w > 0;
  };
  let welded = 0;
  for (let v = n0; v < nV; v++) {
    if (!used[v]) continue;
    const x = oPos[3 * v], z = oPos[3 * v + 2], i = Math.floor(x / WELD_M), j = Math.floor(z / WELD_M);
    const sk = skirtF(v);
    let to = -1;
    if (!sk) {
      // the nearest drawn lattice node (the skirt ring excepted)
      const xs = T.xs, zs = T.zs, g = T.g;
      let ni = Math.min(g - 2, Math.max(1, Math.round((x - xs[1]) / (xs[2] - xs[1])) + 1));
      let nj = Math.min(g - 2, Math.max(1, Math.round((z - zs[1]) / (zs[2] - zs[1])) + 1));
      if (Math.abs(xs[ni] - x) <= WELD_NODE_M && Math.abs(zs[nj] - z) <= WELD_NODE_M) to = nj * g + ni;
    }
    for (let di = -1; di <= 1 && to < 0; di++) {
      for (let dj = -1; dj <= 1 && to < 0; dj++) {
        const L = G.get(key(i + di, j + dj));
        if (L) for (const u of L) if (Math.abs(oPos[3 * u] - x) <= WELD_M && Math.abs(oPos[3 * u + 2] - z) <= WELD_M && skirtF(u) === sk) { to = u; break; }
      }
    }
    if (to >= 0) { rep[v] = to; welded++; continue; }
    const k = key(i, j), L = G.get(k);
    if (L) L.push(v); else G.set(k, [v]);
  }
  res.stats.paveWelded = welded;
  // remap; per output triangle of the kept mesh: its entry (-1: a template
  // run) and label slot, and whether it stays (a collapsed one does not)
  const nK = outTris, keep = new Uint8Array(nK), ent = new Int32Array(nK), slot = new Uint8Array(nK);
  const I = new Uint32Array(nK * 3);
  {
    let r = 0, from = 0;
    const tmpl = (q) => { ent[q] = -1; keep[q] = 1; };
    for (let f = 0; f < cut.tris.length; f++) {
      const t = cut.tris[f];
      for (let q = r; q < r + (t - from); q++) tmpl(q);
      r += t - from; from = t + 1;
      const L = entryLabN[f];
      for (let c = 0; c < L.length; c++) for (let e = 0; e < L[c]; e++, r++) { ent[r] = f; slot[r] = c; }
    }
    for (let q = r; q < nK; q++) tmpl(q);
    for (let q = 0; q < nK; q++) {
      const a = rep[oIdx[3 * q]], b = rep[oIdx[3 * q + 1]], d = rep[oIdx[3 * q + 2]];
      I[3 * q] = a; I[3 * q + 1] = b; I[3 * q + 2] = d;
      if (ent[q] >= 0) keep[q] = a !== b && b !== d && d !== a ? 1 : 0;
    }
  }
  // THE SLIVER CLEAN: a re-tessellated triangle (or a back-to-back pair on
  // one vertex set) under SLIVER_A2 m2 (twice its plan area) that the mesh
  // does not need — its removal leaves fewer open + duplicate directed edges
  // among the edges it holds — is dropped (a fallback's collinear rest, two
  // pieces' bubble between near twins). TWO PASSES: first only the drops
  // that leave every edge they held closed and single, then the greedy
  // "fewer" rule over what is left — greedy alone, where both pieces draw
  // needles along one kerb, could drop a needed sliver first (its edges
  // still twinned by the other piece's needles) and leave a hole the walls
  // then close against nothing (34_51: open 2 / dup 1). pave.sliverGreedy:
  // the one greedy pass (check-pave --twin-sliver)
  const SLIVER_A2 = 1e-4;
  const ek = (a, b) => a * 4194304 + b, EC = new Map();
  for (let q = 0; q < nK; q++) if (keep[q]) for (let c = 0; c < 3; c++) { const k = ek(I[3 * q + c], I[3 * q + (c + 1) % 3]); EC.set(k, (EC.get(k) || 0) + 1); }
  const P = oPos, a2 = (q) => {
    const a = I[3 * q], b = I[3 * q + 1], c = I[3 * q + 2];
    return (P[3 * b] - P[3 * a]) * (P[3 * c + 2] - P[3 * a + 2]) - (P[3 * b + 2] - P[3 * a + 2]) * (P[3 * c] - P[3 * a]);
  };
  const tiny = [];
  for (let q = 0; q < nK; q++) if (keep[q] && ent[q] >= 0 && Math.abs(a2(q)) < SLIVER_A2) tiny.push(q);
  const bySet = new Map(), setKey = (q) => [I[3 * q], I[3 * q + 1], I[3 * q + 2]].sort((x, y) => x - y).join(',');
  for (const q of tiny) { const k = setKey(q); const L = bySet.get(k); if (L) L.push(q); else bySet.set(k, [q]); }
  const bad = (pairs) => {          // open + duplicate directed edges among these vertex pairs
    let n = 0;
    for (const [u, v] of pairs) {
      const c1 = EC.get(ek(u, v)) || 0, c2 = EC.get(ek(v, u)) || 0;
      n += Math.max(0, c1 - 1) + Math.max(0, c2 - 1) + ((c1 > 0) !== (c2 > 0) ? 1 : 0);
    }
    return n;
  };
  let cleaned = 0;
  for (const exact of greedy ? [false] : [true, false]) for (const q of tiny) {
    if (!keep[q]) continue;
    const grp = (bySet.get(setKey(q)) || [q]).filter((x) => keep[x]);
    const pairs = [];
    for (const x of grp) for (let c = 0; c < 3; c++) {
      const u = I[3 * x + c], v = I[3 * x + (c + 1) % 3];
      pairs.push(u < v ? [u, v] : [v, u]);
    }
    const before = bad(pairs);
    if (!before) continue;
    const step = (dv) => { for (const x of grp) for (let c = 0; c < 3; c++) { const k = ek(I[3 * x + c], I[3 * x + (c + 1) % 3]); EC.set(k, (EC.get(k) || 0) + dv); } };
    step(-1);
    if (exact ? bad(pairs) === 0 : bad(pairs) < before) { for (const x of grp) keep[x] = 0; cleaned += grp.length; } else step(1);
  }
  // THE NEEDLE BUBBLE: two pieces' near twins on one line (vertices a
  // fraction of a mm apart, each a hair off a line both sides draw straight)
  // leave a bubble of needles — one piece's needles over it, the other's
  // inverted under it, every edge of it held twice or not at all. The
  // connected tiny triangles still kept (sharing an edge) are dropped
  // together when that leaves every edge they held closed and single
  // (0 open + duplicate), never otherwise; a component that cannot (a strip
  // of needed slivers along a kerb holds the bubble) tries, per duplicate
  // edge of it, its triangles lying on that edge's segment (every corner
  // within BUBBLE_M of it) under the same rule
  {
    const BUBBLE_M = 1e-4;
    const uk = (a, b) => (a < b ? a * 4194304 + b : b * 4194304 + a), byE = new Map();
    const live = tiny.filter((q) => keep[q]);
    for (const q of live) for (let c = 0; c < 3; c++) { const k = uk(I[3 * q + c], I[3 * q + (c + 1) % 3]); const L = byE.get(k); if (L) L.push(q); else byE.set(k, [q]); }
    const seen = new Set();
    for (const q0 of live) {
      if (seen.has(q0) || !keep[q0]) continue;
      const comp = [q0];
      seen.add(q0);
      for (let i = 0; i < comp.length; i++) {
        const q = comp[i];
        for (let c = 0; c < 3; c++) for (const x of byE.get(uk(I[3 * q + c], I[3 * q + (c + 1) % 3]))) if (!seen.has(x) && keep[x]) { seen.add(x); comp.push(x); }
      }
      if (comp.length < 2) continue;
      // the whole component, else per duplicate edge (u, v) of it the
      // component's triangles lying on the segment u-v (every corner within
      // BUBBLE_M of it: the needles of that one line)
      const tryDrop = (set) => {
        const pairs = [];
        for (const x of set) for (let c = 0; c < 3; c++) { const u = I[3 * x + c], v = I[3 * x + (c + 1) % 3]; pairs.push(u < v ? [u, v] : [v, u]); }
        if (!bad(pairs)) return false;
        const step = (dv) => { for (const x of set) for (let c = 0; c < 3; c++) { const k = ek(I[3 * x + c], I[3 * x + (c + 1) % 3]); EC.set(k, (EC.get(k) || 0) + dv); } };
        step(-1);
        if (bad(pairs) === 0) { for (const x of set) keep[x] = 0; cleaned += set.length; res.stats.paveBubbles = (res.stats.paveBubbles || 0) + 1; return true; }
        step(1);
        return false;
      };
      if (tryDrop(comp)) continue;
      const onSeg = (w, u, v) => {
        const ax = P[3 * u], az = P[3 * u + 2], dx = P[3 * v] - ax, dz = P[3 * v + 2] - az, L2 = dx * dx + dz * dz;
        const t = L2 > 0 ? Math.max(0, Math.min(1, ((P[3 * w] - ax) * dx + (P[3 * w + 2] - az) * dz) / L2)) : 0;
        return Math.hypot(P[3 * w] - ax - t * dx, P[3 * w + 2] - az - t * dz) <= BUBBLE_M;
      };
      const tried = new Set();
      for (const x of comp) for (let c = 0; c < 3; c++) {
        if (!keep[x]) break;
        const u = I[3 * x + c], v = I[3 * x + (c + 1) % 3], k = uk(u, v);
        if (tried.has(k) || ((EC.get(ek(u, v)) || 0) < 2 && (EC.get(ek(v, u)) || 0) < 2)) continue;
        tried.add(k);
        const set = comp.filter((y) => keep[y] && onSeg(I[3 * y], u, v) && onSeg(I[3 * y + 1], u, v) && onSeg(I[3 * y + 2], u, v));
        if (set.length >= 2) tryDrop(set);
      }
    }
    // THE T-NEEDLE: a vertex v a hair off the edge w-u of a triangle T of
    // the same entry piece (a walk corner on the line its edge continues
    // along) that T does not hold; a needle N = (u, w, v), inverted, closes
    // it (N wound against T: v lies on T's side of the line, under T) —
  // watertight, but T's edge runs straight past v, so the surface
    // along it is not the line through v (a vertical sliver face). T splits
    // at v into (w, v, c) + (v, u, c), N's slot takes the second: the same
    // directed edges, no needle
    const owner = new Map();
    for (let q = 0; q < nK; q++) if (keep[q] && ent[q] >= 0) for (let c = 0; c < 3; c++) owner.set(ek(I[3 * q + c], I[3 * q + (c + 1) % 3]), q);
    let tsplit = 0;
    for (const q of tiny) {
      if (!keep[q]) continue;
      for (let c = 0; c < 3; c++) {
        const u = I[3 * q + c], w = I[3 * q + (c + 1) % 3], v = I[3 * q + (c + 2) % 3];
        const t = owner.get(ek(w, u));
        if (t === undefined || t === q || !keep[t] || ent[t] !== ent[q] || slot[t] !== slot[q] || Math.abs(a2(t)) < SLIVER_A2 || !(a2(t) * a2(q) < 0)) continue;
        const ax = P[3 * w], az = P[3 * w + 2], dx = P[3 * u] - ax, dz = P[3 * u + 2] - az, L2 = dx * dx + dz * dz;
        const s = ((P[3 * v] - ax) * dx + (P[3 * v + 2] - az) * dz) / L2;
        if (!(s > 1e-6 && s < 1 - 1e-6) || Math.hypot(P[3 * v] - ax - s * dx, P[3 * v + 2] - az - s * dz) > BUBBLE_M) continue;
        let k = 0;
        while (I[3 * t + k] !== w) k++;
        const cc = I[3 * t + (k + 2) % 3];
        for (const x of [t, q]) for (let e = 0; e < 3; e++) owner.delete(ek(I[3 * x + e], I[3 * x + (e + 1) % 3]));
        I[3 * t] = w; I[3 * t + 1] = v; I[3 * t + 2] = cc;
        I[3 * q] = v; I[3 * q + 1] = u; I[3 * q + 2] = cc;
        for (const x of [t, q]) for (let e = 0; e < 3; e++) owner.set(ek(I[3 * x + e], I[3 * x + (e + 1) % 3]), x);
        tsplit++;
        break;
      }
    }
    res.stats.paveTSplit = tsplit;
  }
  res.stats.paveSliverDropped = cleaned;
  // compact: the kept triangles; the entries' counts follow
  const out = new Uint32Array(oIdx.length);
  let w = 0, dropped = 0;
  for (let q = 0; q < nK; q++) {
    if (!keep[q]) { entryLabN[ent[q]][slot[q]]--; cut.nOut[ent[q]]--; dropped++; continue; }
    out[w++] = I[3 * q]; out[w++] = I[3 * q + 1]; out[w++] = I[3 * q + 2];
  }
  res.idx = out.subarray(0, w);
  res.stats.trisOut = w / 3;
  res.stats.paveWeldDropped = dropped;
}

// THE PAVED HEIGHTS AND THE KERB FACE (cutTile with pave cells, after
// the output is assembled). Per output triangle its label (template
// triangles outside every entry are ground); every vertex of a road / walk
// triangle takes pave.at's height there (a vertex both paved and ground —
// a formation edge, a walk's outer edge — is ONE vertex: the territory meets
// the paved height; a vertex both road and walk takes the walk top and the
// road side a COPY at road level). Every edge a road triangle shares with a
// non-road side whose heights differ there is closed by a vertical wall
// (the kerb face: two vertex rows at the same xz), facing the lower side.
// A platform triangle's vertices take its plane; a vertex it shares with a
// road / walk keeps their level and the platform a PLATFORM COPY at the
// plane, and one on a track-facing side (pave.edge) keeps the BED — the
// support nodes of its source triangle outside every ring (pave.plat),
// weighted — with the platform copy above: the wall is the platform's edge
// face, down to the bed. Platforms never yield (the grid stands on the
// plane: game/pave.js platformGround).
// res gains: pave { tlab (per output triangle: 0 ground, PAVE_ROAD,
// PAVE_WALK, PAVE_PLAT, PAVE_FLIGHT — a flight lane's triangle is ground at
// the template's height), roadCopy, flightCopy, platCopy, walls [start,
// count) in idx, miss (vertices pave.at could not place) }; idx grows by
// the walls, pos / uv / h by the copies.
const T_PLAT = 8, T_GROUND = 16;   // paveFinish's touch bits past the labels'
function paveFinish(res, T, ox, oz, pave, nTri, entryLabN, hT, bores = null) {
  const { cut } = res;
  let oPos = res.pos, oUv = res.uv, oH = res.h, oIdx = res.idx;
  const nV = oH.length, entries = cut.tris;
  paveWeld(res, T, entryLabN, pave.sliverGreedy === true);
  oIdx = res.idx;
  const outTris = oIdx.length / 3;
  // per output triangle: its label
  const tlab = new Uint8Array(outTris);
  let w = 0, from = 0;
  for (let f = 0; f < entries.length; f++) {
    const t = entries[f];
    w += t - from; from = t + 1;
    const [a, b, c, d, e] = entryLabN[f];
    tlab.fill(PAVE_ROAD, w + a, w + a + b);
    tlab.fill(PAVE_WALK, w + a + b, w + a + b + c);
    tlab.fill(PAVE_FLIGHT, w + a + b + c, w + a + b + c + d);
    tlab.fill(PAVE_PLAT, w + a + b + c + d, w + a + b + c + d + e);
    w += a + b + c + d + e;
  }
  // the vertices the labelled triangles touch (bit 1 road, bit 2 walk, bit 4
  // flight, bit 8 platform, bit 16 ground)
  const touch = new Uint8Array(nV);
  for (let q = 0; q < outTris; q++) {
    const L = tlab[q], B = L === PAVE_PLAT ? T_PLAT : L || T_GROUND;
    touch[oIdx[3 * q]] |= B; touch[oIdx[3 * q + 1]] |= B; touch[oIdx[3 * q + 2]] |= B;
  }
  // heights: the shared vertex at the walk top where a walk touches it — or
  // lies within pave.at's 1 mm of a walk (the end of a road sliver between a
  // walk's outer line and a road cell's side at a hair's angle: the ground
  // beyond meets the walk's edge, not the sliver's road level) — else at
  // road level; the road copy where road level differs from that
  // (a flight's lane: its triangles keep the template's height — the
  // flight's own ground — and where one shares a vertex with a road / walk
  // triangle it reads a FLIGHT COPY at that height: a wall joins the two)
  // PAVE YIELDS (game/pave.js paveAt): a road / walk level more than
  // pave.yieldM off the template (the conformed ground, the server's grid)
  // is a cell the corridor did not pave — a footprint / structure the
  // conform kept at grid height — so the vertex keeps the template there
  // (client and server agree; no slot), counted in yielded, not a miss
  // — judged on the GRID NODES the template is made of there (the vertex's
  // support: a lattice vertex itself; a new vertex the corners of its
  // source triangle with weight > 0 — an edge point its edge's two ends):
  // a level yields only when it is more than yieldM off every one, so a
  // road edge whose outer node falls away keeps its inner node and stays
  // paved, and a cell wholly at a footprint's height keeps the template
  const yieldM = pave.yieldM, sup = new Float64Array(3), TI = T.idx, vTri = res.cut.vTri, vBary = res.cut.vBary;
  const yl = (y, v) => {
    if (yieldM == null || !Number.isFinite(y) || !hT) return y;
    if (v < T.count) { sup[0] = hT[v]; sup[1] = NaN; sup[2] = NaN; }
    else {
      const n = v - T.count, t = vTri[n];
      for (let c = 0; c < 3; c++) sup[c] = vBary[3 * n + c] > 0 ? hT[TI[3 * t + c]] : NaN;
    }
    return yieldsTo(sup, y, yieldM) ? NaN : y;
  };
  // the bed under a vertex on a track-facing platform side: its support
  // nodes outside every ring, weighted (none: the template's own)
  const bedOf = (v) => {
    if (v < T.count || !hT) return oH[v];
    const n = v - T.count, t = vTri[n];
    let sw = 0, sy = 0;
    for (let c = 0; c < 3; c++) {
      const k = TI[3 * t + c], b = vBary[3 * n + c];
      if (!(b > 0) || pave.plat(ox + T.pos[3 * k], oz + T.pos[3 * k + 2])) continue;
      sw += b; sy += b * hT[k];
    }
    return sw > 0 ? sy / sw : oH[v];
  };
  const anyPlat = pave.cells.some((c) => c.kind === 4);
  const roadCopy = new Int32Array(nV).fill(-1), flightCopy = new Int32Array(nV).fill(-1), platCopy = new Int32Array(nV).fill(-1);
  const copyH = [];
  let miss = 0, nCopy = 0, yielded = 0, bed = 0;
  const yieldV = new Uint8Array(nV);   // per vertex: a road / walk level yielded there
  for (let v = 0; v < nV; v++) {
    if (!(touch[v] & (PAVE_ROAD | PAVE_WALK | T_PLAT))) continue;
    const wx = ox + oPos[3 * v], wz = oz + oPos[3 * v + 2];
    const y0 = oH[v];
    const yp = touch[v] & T_PLAT ? pave.at(PAVE_PLAT, wx, wz) : NaN;
    const okP = Number.isFinite(yp);
    if ((touch[v] & T_PLAT) && !okP) miss++;
    const yr0 = touch[v] & PAVE_ROAD ? pave.at(PAVE_ROAD, wx, wz) : NaN;
    const yw0 = touch[v] & (PAVE_ROAD | PAVE_WALK) ? pave.at(PAVE_WALK, wx, wz) : NaN;
    const yr = yl(yr0, v), yw = yl(yw0, v);
    const okR = Number.isFinite(yr), okW = Number.isFinite(yw);
    const yR = Number.isFinite(yr0) && !okR, yW = Number.isFinite(yw0) && !okW;
    if (yR || yW) { yielded++; yieldV[v] = 1; }
    if (okR || okW) {
      oH[v] = okW ? (okR ? Math.max(yr, yw) : yw) : yr;
      if (okR && Math.fround(yr) !== oH[v]) { roadCopy[v] = nV + nCopy++; copyH.push(yr); }
      else if ((touch[v] & PAVE_ROAD) && !okR && !yR) miss++;
    } else if (okP) {
      // a platform's own vertex: the plane; on a track-facing side met by
      // the ground (or a flight's lane) the ground keeps the bed
      if ((touch[v] & (T_GROUND | PAVE_FLIGHT)) && pave.edge(wx, wz)) { oH[v] = bedOf(v); bed++; } else oH[v] = yp;
    } else {
      if ((touch[v] & (PAVE_ROAD | PAVE_WALK)) && !yR && !yW) miss++;
      continue;
    }
    if (okP && Math.fround(yp) !== oH[v]) { platCopy[v] = nV + nCopy++; copyH.push(yp); }
    if ((touch[v] & PAVE_FLIGHT) && y0 !== oH[v]) { flightCopy[v] = nV + nCopy++; copyH.push(y0); }
  }
  // THE GROUND BESIDE A TRACK-FACING SIDE: a new ground vertex off the ring
  // whose source triangle holds a grid node inside a ring (the plane) and
  // whose nearest side of those rings is track-facing — a flight lane's /
  // a bore's line crossing the cell between the side and the outer nodes —
  // takes the bed too (the grid's rule: game/pave.js platformGround); a
  // flight's lane there keeps its ground in a flight copy
  if (anyPlat && hT) for (let v = T.count; v < nV; v++) {
    if (!(touch[v] & T_GROUND) || (touch[v] & (PAVE_ROAD | PAVE_WALK | T_PLAT))) continue;
    const n = v - T.count, t = vTri[n], held = [];
    for (let c = 0; c < 3; c++) {
      const k = TI[3 * t + c];
      if (!(vBary[3 * n + c] > 0)) continue;
      const q = pave.platAt(ox + T.pos[3 * k], oz + T.pos[3 * k + 2]);
      if (q >= 0 && !held.includes(q)) held.push(q);
    }
    const wx = ox + oPos[3 * v], wz = oz + oPos[3 * v + 2];
    if (!held.length || pave.platAt(wx, wz) >= 0 || !pave.bedSide(wx, wz, held)) continue;
    const y0 = oH[v], yb = bedOf(v);
    if (yb === y0) continue;
    oH[v] = yb; bed++;
    if (touch[v] & PAVE_FLIGHT) { flightCopy[v] = nV + nCopy++; copyH.push(y0); }
  }
  // THE TERRITORY MEETS THE EDGE: a new vertex no paved triangle touches but
  // within pave.at's tolerance (1 mm) of a paved cell — the far end of a
  // ground sliver between two near-coincident cell lines (a walk's outer
  // line and a road cell's side crossing at a hair's angle) — takes the
  // paved height there, else the sliver is a vertical ground face at the edge
  // (never a flight's: its lane keeps its ground; a platform's plane
  // unless on a track-facing side: there the ground keeps the bed)
  let lift = 0, liftMax = 0;
  for (let v = T.count; v < nV; v++) {
    if (touch[v] & ~T_GROUND) continue;
    const wx = ox + oPos[3 * v], wz = oz + oPos[3 * v + 2];
    const yr0 = pave.at(PAVE_ROAD, wx, wz), yw0 = pave.at(PAVE_WALK, wx, wz);
    const yp = anyPlat ? pave.at(PAVE_PLAT, wx, wz) : NaN, okP = Number.isFinite(yp) && !pave.edge(wx, wz);
    const yr = yl(yr0, v), yw = yl(yw0, v);
    const okR = Number.isFinite(yr), okW = Number.isFinite(yw);
    if ((Number.isFinite(yr0) && !okR) || (Number.isFinite(yw0) && !okW)) yieldV[v] = 1;
    if (!okR && !okW && !okP) continue;
    const y = okW ? (okR ? Math.max(yr, yw) : yw) : okR ? yr : yp;
    liftMax = Math.max(liftMax, Math.abs(y - oH[v])); lift++;
    oH[v] = y;
  }
  // THE WALLS. A triangle's version of vertex v: its road copy (a road
  // triangle), its flight copy (a flight's), else v. Where two triangles
  // share an edge u - v but read different versions of an end, the wall
  // quad joins them: X's edge ux -> vx over Y's vy -> uy is (vx, ux, uy, vy)
  // — it holds vx -> ux (X's twin) and uy -> vy (Y's twin), so the surface
  // stays one oriented manifold and the face looks at the lower side (a
  // graph's step: whichever side is higher). Built once per edge, from the
  // side of higher rank (flight 2 > road 1 > the rest 0); an edge with no
  // twin (the re-meshed region's rim) walls against the shared vertices.
  // (flight 3 > platform 2 > road 1 > the rest 0)
  const ek = (a, b) => a * 4194304 + b;
  const rank = (L) => (L === PAVE_FLIGHT ? 3 : L === PAVE_PLAT ? 2 : L === PAVE_ROAD ? 1 : 0);
  const verOf = (L, v) => (L === PAVE_ROAD && roadCopy[v] >= 0 ? roadCopy[v] : L === PAVE_FLIGHT && flightCopy[v] >= 0 ? flightCopy[v]
    : L === PAVE_PLAT && platCopy[v] >= 0 ? platCopy[v] : v);
  const hasCopy = (v) => roadCopy[v] >= 0 || flightCopy[v] >= 0 || platCopy[v] >= 0;
  const edgeT = new Map();             // directed edge -> its triangle (triangles at a copied vertex)
  for (let q = 0; q < outTris; q++) {
    const a = oIdx[3 * q], b = oIdx[3 * q + 1], c = oIdx[3 * q + 2];
    if (!hasCopy(a) && !hasCopy(b) && !hasCopy(c)) continue;
    edgeT.set(ek(a, b), q); edgeT.set(ek(b, c), q); edgeT.set(ek(c, a), q);
  }
  const wall = [];
  for (let q = 0; q < outTris; q++) {
    const LX = tlab[q], rX = rank(LX);
    if (!rX) continue;
    for (let c = 0; c < 3; c++) {
      const u = oIdx[3 * q + c], v = oIdx[3 * q + (c + 1) % 3];
      if (!hasCopy(u) && !hasCopy(v)) continue;
      const y = edgeT.get(ek(v, u));
      const LY = y === undefined ? 0 : tlab[y];
      if (LY === LX || rank(LY) > rX) continue;
      const ux = verOf(LX, u), vx = verOf(LX, v), uy = verOf(LY, u), vy = verOf(LY, v);
      if (ux !== uy) wall.push(vx, ux, uy);
      if (vx !== vy) wall.push(vx, uy, vy);
    }
  }
  // the road, flight and platform triangles read their copies
  for (let q = 0; q < outTris; q++) {
    const L = tlab[q];
    if (L !== PAVE_ROAD && L !== PAVE_FLIGHT && L !== PAVE_PLAT) continue;
    for (let c = 0; c < 3; c++) oIdx[3 * q + c] = verOf(L, oIdx[3 * q + c]);
  }
  // THE SEAL. Two independent geometries meet on paved ground (a walk ring
  // from the WORLD stage, the road quads from the drawn pieces): where a
  // walk's line passes a road's line or corner by a fraction of a mm the two
  // pieces can end on distinct points that no tolerance makes one, and the
  // mesh keeps a crack along them. Every open boundary left inside the
  // re-meshed region (an edge of a re-tessellated triangle or a wall whose
  // twin is missing; a template edge between two template corners faces an
  // untouched template triangle; the lattice's outer skirt is the tile's
  // border) is chained into loops, and each loop is closed by a fan of its
  // own vertices — no new vertex, oriented as the ground. paveFinish
  // reports the loops and their largest plan extent (check-pave bounds it).
  // A BORE'S OPENING IS NO CRACK (portal-skirt): the rim of the hole a bore
  // cut lies on its volume's CLEAR_M-grown surface; an open edge there (both
  // ends and its midpoint within SEAL_RIM_M of it) stays open — the seal
  // fanned every portal of a paved tile shut, a ground-textured curtain in
  // the mouth. pave.sealBoreRims (check-bore-cut's red twin only) restores
  // that.
  const srcV = new Int32Array(nCopy);
  for (let v = 0; v < nV; v++) for (const k of [roadCopy[v], flightCopy[v], platCopy[v]]) if (k >= 0) srcV[k - nV] = v;
  const onCut = bores && !pave.sealBoreRims
    ? (x, y, z) => Math.abs(depthInAll(bores, ox + x, y, oz + z) + CLEAR_M) <= SEAL_RIM_M : null;
  const seal = sealCracks(T, oIdx, outTris, wall, cut, nV, oPos, srcV, (v) => (v >= nV ? copyH[v - nV] : oH[v]), onCut);
  for (const v of seal.tris) wall.push(v);
  // grow the arrays: copies (xz / uv of their source), walls after the mesh
  if (nCopy) {
    const p2 = new Float32Array((nV + nCopy) * 3), u2 = new Float32Array((nV + nCopy) * 2), h2 = new Float32Array(nV + nCopy);
    p2.set(oPos); u2.set(oUv); h2.set(oH);
    for (let v = 0; v < nV; v++) {
      for (const k of [roadCopy[v], flightCopy[v], platCopy[v]]) {
        if (k < 0) continue;
        p2[3 * k] = oPos[3 * v]; p2[3 * k + 1] = oPos[3 * v + 1]; p2[3 * k + 2] = oPos[3 * v + 2];
        u2[2 * k] = oUv[2 * v]; u2[2 * k + 1] = oUv[2 * v + 1];
        h2[k] = copyH[k - nV];
      }
    }
    oPos = p2; oUv = u2; oH = h2;
  }
  const i2 = new Uint32Array(oIdx.length + wall.length);
  i2.set(oIdx);
  i2.set(wall, oIdx.length);
  res.pos = oPos; res.uv = oUv; res.h = oH; res.idx = i2;
  res.pave = { tlab, roadCopy, flightCopy, platCopy, nV, nCopy, walls: [outTris, wall.length / 3 - seal.tris.length / 3], seal: [outTris + wall.length / 3 - seal.tris.length / 3, seal.tris.length / 3],
    sealLoops: seal.loops, sealColumns: seal.columns, sealMaxM: seal.maxExtent, sealGapM: seal.maxGap, sealGaps: seal.gaps, sealOpen: seal.open, sealRims: seal.rims, miss, yielded, yieldV, entryLabN };
  res.stats.paveCopies = nCopy; res.stats.paveBed = bed; res.stats.paveWalls = wall.length / 3 - seal.tris.length / 3; res.stats.paveMiss = miss;
  res.stats.paveEdgeLift = lift; res.stats.paveEdgeLiftMaxM = liftMax;
  res.stats.sealColumns = seal.columns; res.stats.sealLoops = seal.loops; res.stats.sealTris = seal.tris.length / 3; res.stats.sealMaxM = seal.maxExtent; res.stats.sealGapM = seal.maxGap; res.stats.sealGaps = seal.gaps; res.stats.sealOpen = seal.open; res.stats.sealRims = seal.rims; res.stats.paveYield = yielded;
  res.stats.bytes = oPos.byteLength + oUv.byteLength + oH.byteLength + i2.byteLength;
}

// paveFinish's seal: the open boundary loops inside the re-meshed region,
// each closed by a fan. -> { tris (flat), loops, maxExtent (m, the largest
// loop's plan extent), maxGap (m, the widest crack: the largest height of a
// fan triangle over its longest side, in 3D — a loop between two float32
// twins of one point is a sliver of no width), gaps (loops wider than
// SEAL_GAP_M), open (open edges left: a chain that does not close) }
// a crack the seal closes that is no wider than the drawn tolerance (check-pave
// R2: 5 mm) changes nothing a player can see: its fan stays within it
const SEAL_GAP_M = 0.005;
// a vertex ON a bore's cut surface: its depth -CLEAR_M within this, the
// drawn tolerance (check-pave R2: 5 mm). The rim of a paved portal tile
// measures -0.5 +- 0.1 mm (float32 xz / y); a rim vertex the pave placed
// lies up to 2.3 mm off (bore 8's far portal, bake 304e0733)
const SEAL_RIM_M = 0.005;
// onCut (x, y, z tile-local) -> true on a bore's cut surface: an open edge
// with both ends and its midpoint there is a bore opening's rim, never sealed
function sealCracks(T, oIdx, outTris, wall, cut, nV, P, srcV = null, hOf = null, onCut = null) {
  const ek = (a, b) => a * 4194304 + b;
  const E = new Map();               // directed edge -> 1, over entries' triangles and walls
  const tris = cut.tris, nOut = cut.nOut;
  const each = (f) => {
    let w = 0, from = 0;
    for (let e = 0; e < tris.length; e++) {
      w += tris[e] - from; from = tris[e] + 1;
      for (let q = w; q < w + nOut[e]; q++) f(oIdx[3 * q], oIdx[3 * q + 1], oIdx[3 * q + 2]);
      w += nOut[e];
    }
    for (let q = 0; q < wall.length; q += 3) f(wall[q], wall[q + 1], wall[q + 2]);
  };
  each((a, b, c) => { E.set(ek(a, b), 1); E.set(ek(b, c), 1); E.set(ek(c, a), 1); });
  const n0 = T.count;
  const out = new Map();             // start vertex -> open edges' ends
  let nOpen = 0;
  const tmplEdge = (a, b) => {
    if (a >= n0 || b >= n0) return false;
    const g = T.g, ai = a % g, aj = (a - ai) / g, bi = b % g, bj = (b - bi) / g;
    return Math.abs(ai - bi) <= 1 && Math.abs(aj - bj) <= 1;
  };
  // (a copy stands at its source's xz)
  const sv = (v) => (v >= nV && srcV ? srcV[v - nV] : v);
  const vx = (v) => P[3 * sv(v)], vz = (v) => P[3 * sv(v) + 2];
  const rim = (a, b) => onCut(vx(a), hOf(a), vz(a)) && onCut(vx(b), hOf(b), vz(b))
    && onCut((vx(a) + vx(b)) / 2, (hOf(a) + hOf(b)) / 2, (vz(a) + vz(b)) / 2);
  let rims = 0;
  each((a, b, c) => {
    for (const [u, v] of [[a, b], [b, c], [c, a]]) {
      if (E.has(ek(v, u)) || tmplEdge(u, v)) continue;
      if (onCut && hOf && rim(u, v)) { rims++; continue; }
      let L = out.get(u);
      if (!L) out.set(u, L = []);
      L.push(v); nOpen++;
    }
  });
  const fill = [];
  let loops = 0, maxExtent = 0, left = 0, columns = 0, maxGap = 0, gaps = 0;
  for (const [s] of out) {
    for (;;) {
      const L0 = out.get(s);
      if (!L0 || !L0.length) break;
      // follow open edges from s until back at s
      const loop = [s];
      let u = s, ok = false;
      for (let guard = 0; guard < 4096; guard++) {
        const L = out.get(u);
        if (!L || !L.length) break;
        const v = L.pop();
        if (v === s) { ok = true; break; }
        loop.push(v); u = v;
      }
      if (!ok || loop.length < 3) { left += loop.length; continue; }
      // A COLUMN, not a crack: the versions of ONE vertex (shared, road
      // copy, flight copy) where three walls meet on its vertical line —
      // closed by a triangle of no plan extent
      if (loop.every((v) => sv(v) === sv(loop[0]))) {
        columns++;
        for (let i = 1; i + 1 < loop.length; i++) fill.push(loop[0], loop[i + 1], loop[i]);
        continue;
      }
      loops++;
      let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
      for (const v of loop) { x0 = Math.min(x0, vx(v)); x1 = Math.max(x1, vx(v)); z0 = Math.min(z0, vz(v)); z1 = Math.max(z1, vz(v)); }
      maxExtent = Math.max(maxExtent, Math.hypot(x1 - x0, z1 - z0));
      // the loop runs along existing edges v_i -> v_i+1: the fan holds
      // their twins (v0, v_i+1, v_i)
      let gap = 0;
      for (let i = 1; i + 1 < loop.length; i++) {
        const a = loop[0], b = loop[i + 1], c = loop[i];
        fill.push(a, b, c);
        if (!hOf) continue;
        const bx = vx(b) - vx(a), by = hOf(b) - hOf(a), bz = vz(b) - vz(a), cx = vx(c) - vx(a), cy = hOf(c) - hOf(a), cz = vz(c) - vz(a);
        const A2 = Math.hypot(by * cz - bz * cy, bz * cx - bx * cz, bx * cy - by * cx);
        const L = Math.max(Math.hypot(bx, by, bz), Math.hypot(cx, cy, cz), Math.hypot(cx - bx, cy - by, cz - bz));
        if (L > 0 && A2 / L > gap) gap = A2 / L;
      }
      if (gap > maxGap) maxGap = gap;
      if (gap > SEAL_GAP_M) gaps++;
    }
  }
  return { tris: fill, loops, columns, maxExtent, maxGap, gaps, open: left, openEdges: nOpen, rims };
}

// THE GRID NODES UNDER A POINT: the template heights (h, per lattice
// vertex) of the lattice triangle holding tile-local (lx, lz) with weight
// > 0 there (a node: itself; an edge point: its edge's ends), the rest NaN,
// into out — the conformed ground the pave yields to (paveFinish judges a
// new vertex by its source triangle the same way; check-pave hands this to
// game/pave.js paveAt as idx.ground). NaN x3 off the drawn quads.
export function latticeSupport(T, h, lx, lz, out = new Float64Array(3)) {
  out[0] = out[1] = out[2] = NaN;
  const xs = T.xs, zs = T.zs, g = T.g;
  if (!(lx >= xs[1] && lx <= xs[g - 2] && lz >= zs[1] && lz <= zs[g - 2])) return out;
  let i = Math.min(g - 3, Math.max(1, Math.floor((lx - xs[1]) / (xs[2] - xs[1])) + 1));
  let j = Math.min(g - 3, Math.max(1, Math.floor((lz - zs[1]) / (zs[2] - zs[1])) + 1));
  while (i > 1 && xs[i] > lx) i--; while (i < g - 3 && xs[i + 1] < lx) i++;
  while (j > 1 && zs[j] > lz) j--; while (j < g - 3 && zs[j + 1] < lz) j++;
  const q = j * (g - 1) + i, P = T.pos, I = T.idx, E = 1e-9;
  for (let t = 2 * q; t <= 2 * q + 1; t++) {
    const a = I[3 * t], b = I[3 * t + 1], c = I[3 * t + 2];
    const ax = P[3 * a], az = P[3 * a + 2], bx = P[3 * b] - ax, bz = P[3 * b + 2] - az, cx = P[3 * c] - ax, cz = P[3 * c + 2] - az;
    const den = bx * cz - bz * cx;
    if (!(Math.abs(den) > 1e-12)) continue;
    const px = lx - ax, pz = lz - az, l1 = (px * cz - pz * cx) / den, l2 = (bx * pz - bz * px) / den, l0 = 1 - l1 - l2;
    if (l0 < -E || l1 < -E || l2 < -E) continue;
    out[0] = l0 > E ? h[a] : NaN; out[1] = l1 > E ? h[b] : NaN; out[2] = l2 > E ? h[c] : NaN;
    return out;
  }
  return out;
}

// a paved level y yields to the grid nodes c: more than m off every finite
// one (none finite: never)
function yieldsTo(c, y, m) {
  let any = false;
  for (let i = 0; i < c.length; i++) { if (!Number.isFinite(c[i])) continue; if (Math.abs(y - c[i]) <= m) return false; any = true; }
  return any;
}

// THE DRAWN SURFACE OF A RE-MESHED TILE at tile-local (lx, lz): the highest
// output triangle holding it (the walk top on a kerb line), y or NaN outside
// the lattice. The output triangles of template triangle t are the entry's
// run (cut.tris ascending, cut.nOut) or t's own slot shifted by the entries
// before it. Walls (zero plan area) are never read. groundm/lookup.js
// stands on the baked region by the same rule (standing = drawn;
// check-groundm compares the two).
export function skinAt(T, cut, lx, lz) {
  const xs = T.xs, zs = T.zs, g = T.g;
  // the drawn quads are 1 .. g - 3 (0 and g - 2 are the skirt's, no plan area)
  if (!(lx >= xs[1] && lx <= xs[g - 2] && lz >= zs[1] && lz <= zs[g - 2])) return NaN;
  let i = Math.min(g - 3, Math.max(1, Math.floor((lx - xs[1]) / (xs[2] - xs[1])) + 1));
  let j = Math.min(g - 3, Math.max(1, Math.floor((lz - zs[1]) / (zs[2] - zs[1])) + 1));
  while (i > 1 && xs[i] > lx) i--; while (i < g - 3 && xs[i + 1] < lx) i++;
  while (j > 1 && zs[j] > lz) j--; while (j < g - 3 && zs[j + 1] < lz) j++;
  const C = cut.cut, tris = C.tris;
  if (!C._cum) {
    // per entry: the output index of its first triangle
    const st = new Int32Array(tris.length);
    let shift = 0;
    for (let f = 0; f < tris.length; f++) { st[f] = tris[f] + shift; shift += C.nOut[f] - 1; }
    C._cum = st;
  }
  const P = cut.pos, H = cut.h, I = cut.idx;
  let best = -Infinity;
  const q = j * (g - 1) + i;
  for (let t = 2 * q; t <= 2 * q + 1; t++) {
    // binary search t in the entries
    let lo = 0, hi = tris.length;
    while (lo < hi) { const m = (lo + hi) >> 1; if (tris[m] < t) lo = m + 1; else hi = m; }
    let s, n;
    if (lo < tris.length && tris[lo] === t) { s = C._cum[lo]; n = C.nOut[lo]; }
    else { s = t + (lo > 0 ? C._cum[lo - 1] + C.nOut[lo - 1] - tris[lo - 1] - 1 : 0); n = 1; }
    for (let o = s; o < s + n; o++) {
      const a = I[3 * o], b = I[3 * o + 1], c = I[3 * o + 2];
      const ax = P[3 * a], az = P[3 * a + 2], bx = P[3 * b] - ax, bz = P[3 * b + 2] - az, cx = P[3 * c] - ax, cz = P[3 * c + 2] - az;
      const den = bx * cz - bz * cx;
      if (!(Math.abs(den) > 1e-12)) continue;
      const px = lx - ax, pz = lz - az;
      const l1 = (px * cz - pz * cx) / den, l2 = (bx * pz - bz * px) / den;
      const E = -1e-6;
      if (l1 < E || l2 < E || l1 + l2 > 1 - E) continue;
      const y = H[a] + l1 * (H[b] - H[a]) + l2 * (H[c] - H[a]);
      if (y > best) best = y;
    }
  }
  return best > -Infinity ? best : NaN;
}
